<template>
    <div>
        <div
            class="field"
            @mousedown="onInternalPointerDown"
            @mouseup="onInternalPointerUp"
            @touchstart="onInternalPointerDown"
            @touchend="onInternalPointerUp"
        >
            <e-taginput
                ref="taginput"
                v-model="selectedTags"
                :data="filteredTags"
                open-on-focus
                autocomplete
                allow-new
                field="name.en"
                class="control"
                @typing="getFilteredTags"
                @add="saveSelectedTags"
                @remove="saveSelectedTags"
                @blur="commitOnBlur($event)"
            >
                <template #default="{ option }">
                    {{ option.name.en }}
                </template>
            </e-taginput>
        </div>
    </div>
</template>

<script>
import find from 'lodash/find.js';

export default {
    name: 'TagSwitcher',

    props: {
        isManualSave: {
            type: Boolean,
            required: false,
        },
        tags: {
            type: Array,
            required: true,
        },

        usesTagType: {
            type: Boolean,
            required: false,
            default: false,
        },

        tagType: {
            type: String,
            required: true,
        },

        subject: {
            type: Object,
            required: true,
        },

        action: {
            type: String,
            required: false,
            default: '',
        },

        modelKey: {
            type: String,
            required: false,
            default: 'id',
        },
    },

    emits: ['syncTags'],

    data: () => ({
        filteredTags: [],
        currentInputText: '',
        selectedTags: [],
        isSaving: false,
        isInteractingWithin: false,
    }),

    computed: {
        currentSubjectTags() {
            return this.subject?.tags ?? [];
        },
    },

    watch: {
        subject() {
            this.loadTags();
        },
    },

    created() {
        this.loadTags();
    },

    methods: {
        getFilteredTags(text) {
            this.currentInputText = text;

            this.filteredTags = this.tags.filter(tag => {
                return (
                    tag.name.en?.toLowerCase().indexOf(text.toLowerCase()) >= 0 &&
                    !find(this.selectedTags, { id: tag.id })
                );
            });
        },

        async saveSelectedTags() {
            if (!this.isManualSave && this.subject.id) {
                this.isSaving = true;

                await this.$store.dispatch('AutoSave/setSaving', this.action);

                await this.$store.dispatch(this.action, {
                    subject: this.subject,
                    tags: this.selectedTags,
                    modelKey: this.modelKey,
                    tagType: this.usesTagType ? this.tagType : null,
                });

                this.isSaving = false;

                await this.$store.dispatch('AutoSave/setSaved', this.action);

                // Keep parent state synchronized to prevent stale tag payloads on full form save.
                this.$emit('syncTags', this.selectedTags);

                this.reset();
            } else {
                // this.subject.tags = this.selectedTags;
                this.$emit('syncTags', this.selectedTags);
            }
        },

        reset() {
            this.filteredTags = [];

            this.currentInputText = '';
        },

        loadTags() {
            const { tags } = this.subject;

            if (!tags || typeof tags !== 'object' || typeof tags === 'function' || Array.isArray(tags) === false) {
                this.selectedTags = [];
            } else if (this.usesTagType) {
                this.selectedTags = tags.filter(tag => tag.type === this.tagType);
            } else {
                this.selectedTags = tags.filter(tag => !tag.type);
            }

            this.getFilteredTags('');
        },

        async commitOnBlur(event) {
            // If the next focused element is still within the tag input component,
            // or if a pointer interaction started inside (e.g., selecting a suggestion),
            // do not commit the partial input.
            const tagInputRoot = this.$refs.taginput && this.$refs.taginput.$el ? this.$refs.taginput.$el : null;
            const nextFocused = (event && (event.relatedTarget || document.activeElement)) || null;

            if (tagInputRoot) {
                if (this.isInteractingWithin) {
                    return;
                }

                if (nextFocused && tagInputRoot.contains(nextFocused)) {
                    return;
                }
            }

            const inputEl = this.getNativeInputEl(event);
            const input = ((inputEl && inputEl.value) || this.currentInputText || '').toString().trim();
            const normalized = this.normalizeInput(input);

            this.clearTagInputElement(event);
            this.currentInputText = '';

            if (!normalized) {
                this.getFilteredTags('');

                return;
            }

            const tagToAdd = this.prepareTagToAdd(input, normalized);

            if (tagToAdd) {
                this.selectedTags = [...this.selectedTags, tagToAdd];

                await this.saveSelectedTags();
            }

            this.getFilteredTags('');
        },

        onInternalPointerDown(event) {
            const tagInputRoot = this.$refs.taginput && this.$refs.taginput.$el ? this.$refs.taginput.$el : null;

            this.isInteractingWithin = !!(tagInputRoot && event && event.target && tagInputRoot.contains(event.target));
        },

        onInternalPointerUp() {
            this.isInteractingWithin = false;
        },

        normalizeInput(text) {
            return (text || '').toString().trim()?.toLowerCase();
        },

        tagMatchesCurrentType(tag) {
            return this.usesTagType ? tag.type === this.tagType : tag.type === null;
        },

        isAlreadySelectedByIdOrName(normalizedName, candidate) {
            if (candidate && candidate.id && find(this.selectedTags, { id: candidate.id })) {
                return true;
            }

            return this.selectedTags.some(existing => {
                const existingName =
                    typeof existing === 'string'
                        ? this.normalizeInput(existing)
                        : this.normalizeInput(existing?.name?.en);

                const typeMatches = typeof existing === 'string' ? true : this.tagMatchesCurrentType(existing);

                return existingName === normalizedName && typeMatches;
            });
        },

        prepareTagToAdd(rawInput, normalizedName) {
            const existing =
                this.tags.find(
                    tag => this.normalizeInput(tag?.name?.en) === normalizedName && this.tagMatchesCurrentType(tag)
                ) || null;

            if (this.isAlreadySelectedByIdOrName(normalizedName, existing)) return null;

            return (
                existing || { id: null, name: { en: rawInput.trim() }, type: this.usesTagType ? this.tagType : null }
            );
        },

        clearTagInputElement(event) {
            const inputEl = this.getNativeInputEl(event);

            if (inputEl) {
                inputEl.value = '';

                try {
                    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                } catch (e) {
                    //
                }
            }
        },

        getNativeInputEl(event) {
            if (event?.target?.tagName === 'INPUT') {
                return event.target;
            }

            if (this.$refs.taginput?.$el) {
                return this.$refs.taginput.$el.querySelector('input');
            }

            return null;
        },
    },
};
</script>
